<template>
  <div
    class="flex h-full w-64 flex-shrink-0 flex-col gap-4 overflow-auto max-md:h-auto max-md:w-full max-md:flex-none"
  >
    <div class="flex flex-col gap-1 px-5 pt-3">
      <h2 class="text-2xl font-bold capitalize-first">
        {{ $t('attributes.commission_note') }}
      </h2>
      <h3 class="-mt-1 text-sm font-medium">
        {{ commissionNote.funeral_home?.name }}
      </h3>
      <div class="w-fit text-xs">
        <BaseLabel
          :content="commissionNote.status_label?.value"
          :color="commissionNote.status_label?.color"
        />
      </div>
    </div>

    <div class="flex flex-col gap-3 px-5" data-test="note-data">
      <div class="flex items-start gap-3">
        <FontAwesomeIcon
          :icon="['fal', 'file-invoice']"
          class="mt-1 w-4 text-gray-400"
        />
        <div>
          <div class="truncate text-xs leading-tight text-gray-500">
            {{ $t('attributes.reference') }}
          </div>
          <div
            class="leading-tight font-medium text-gray-900 dark:text-gray-100"
          >
            {{ commissionNote.reference }}
          </div>
        </div>
      </div>
      <div class="flex items-start gap-3">
        <FontAwesomeIcon
          :icon="['fal', 'calendar']"
          class="mt-1 w-4 text-gray-400"
        />
        <div>
          <div class="truncate text-xs leading-tight text-gray-500">
            {{ $t('attributes.date') }}
          </div>
          <div
            class="leading-tight font-medium text-gray-900 dark:text-gray-100"
          >
            {{ commissionNote.date_dmy }}
          </div>
        </div>
      </div>
      <div v-if="commissionNote.period_label" class="flex items-start gap-3">
        <FontAwesomeIcon
          :icon="['fal', 'calendar-range']"
          class="mt-1 w-4 text-gray-400"
        />
        <div>
          <div class="truncate text-xs leading-tight text-gray-500">
            {{ $t('global.period') }}
          </div>
          <div
            class="leading-tight font-medium text-gray-900 dark:text-gray-100"
          >
            {{ commissionNote.period_label }}
          </div>
        </div>
      </div>
      <!-- as the responsible of an invoice: under its dates, set apart by a line -->
      <div
        v-if="commissionNote.advisor"
        data-test="advisor"
        class="mt-1 border-t border-gray-200 pt-2 dark:border-slate-600"
      >
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('attributes.advisor') }}
        </div>
        <div class="leading-tight font-medium text-gray-900 dark:text-gray-100">
          {{ commissionNote.advisor.full_name }}
        </div>
        <div
          v-if="commissionNote.advisor.roles?.length"
          class="mt-1 flex flex-row flex-wrap gap-1"
        >
          <span
            v-for="role in commissionNote.advisor.roles"
            :key="role"
            class="rounded bg-slate-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-slate-600 dark:text-gray-200"
          >
            {{ roleLabel(role) }}
          </span>
        </div>
      </div>
      <div
        v-if="commissionNote.remarks"
        class="flex flex-col gap-1"
        data-test="remarks"
      >
        <h3 class="mt-2 font-bold capitalize-first">
          {{ $t('attributes.remarks') }}
        </h3>
        <div class="mb-1 flex-1 text-sm whitespace-pre-wrap">
          {{ commissionNote.remarks }}
        </div>
      </div>
      <div
        v-if="commissionNote.internal_note"
        class="flex flex-col gap-1"
        data-test="internal-note"
      >
        <h3 class="mt-2 font-bold capitalize-first">
          {{ $t('attributes.internal_note') }}
        </h3>
        <div class="mb-1 flex-1 text-sm whitespace-pre-wrap">
          {{ commissionNote.internal_note }}
        </div>
      </div>
    </div>

    <CommissionNoteTotals :commission-note="commissionNote" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import BaseLabel from '@c/addf-package/components/BaseLabel/BaseLabel.vue'
import CommissionNoteTotals from '@/views/invoicing/show/commissionNote/components/CommissionNoteTotals.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

/**
 * The left panel of a commission note, read only: its data, its advisor, its
 * remarks and its totals. It stays as it is while the note is edited, until the
 * edition is saved.
 */
export default {
  name: 'CommissionNoteSidePanel',
  components: { BaseLabel, CommissionNoteTotals, FontAwesomeIcon },
  props: {
    commissionNote: { type: Object, required: true }
  },
  computed: {
    ...mapGetters({ roles: 'metadata/getRolesMetadata' })
  },
  methods: {
    roleLabel(name) {
      return this.roles?.find((role) => role.name === name)?.name_index ?? name
    }
  }
}
</script>
